import { useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaCommPreferenceV1Service, serviceName } from '../service';
import { CheckPreferenceIO, ServiceQueries } from '../types';
import { useCommPreferenceV1Queries } from './use-comm-preference-v1-queries';
import { UseCommPreferenceV1QueryEndpointArgs, useCommPreferenceV1Query } from './use-comm-preference-v1-query';

/**
 * A hook that returns a query for the `CheckPreference` endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useCheckPreferenceQuery = <E = unknown, D = CheckPreferenceIO['output']>(
  args: UseCommPreferenceV1QueryEndpointArgs<'CheckPreference', E, D>
) =>
  useCommPreferenceV1Query({
    endpointName: 'CheckPreference',
    ...args,
  });

/**
 * A hook that returns an array of queries for the `CheckPreference` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useCheckPreferenceQueries = <E = unknown, D = CheckPreferenceIO['output']>(
  argsArr: UseCommPreferenceV1QueryEndpointArgs<'CheckPreference', E, D>[]
) =>
  useCommPreferenceV1Queries(
    argsArr.map((args) => ({
      endpointName: 'CheckPreference',
      ...args,
    }))
  );

/**
 * A hook that returns a function to fetch the query for the `CheckPreference` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchCheckPreference = () => {
  const queryClient = useQueryClient();

  const fetchCheckPreference = async <E = unknown, D = CheckPreferenceIO['output']>({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UseCommPreferenceV1QueryEndpointArgs<'CheckPreference', E, D>, 'options'> & {
    options?: LimitedSchemaFetchQueryOptions<
      CheckPreferenceIO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'CheckPreference'>>
    >;
  }) => {
    const resolvedRequest = typeof request === 'function' ? request({}) : request;
    try {
      const response = await queryClient.fetchQuery<
        CheckPreferenceIO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'CheckPreference'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'CheckPreference'>({
          serviceName,
          endpointName: 'CheckPreference',
          request: resolvedRequest,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaCommPreferenceV1Service.CheckPreference(resolvedRequest, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError as D;
      }
      throw error;
    }
  };

  return fetchCheckPreference;
};
